import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Stack,
  Autocomplete,
} from '@mui/material';
import type { LigaInput } from '../../types/liga';
import { buscarUsuarios, type Usuario } from '../../services/usuarioService';
import { criarOrganizadorLiga } from '../../services/organizadorService';

export interface CreateLeagueDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: LigaInput) => Promise<{ id: string }>;
}

const CreateLeagueDialog: React.FC<CreateLeagueDialogProps> = ({
  open,
  onClose,
  onCreate,
}) => {
  const [nome, setNome] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [userOptions, setUserOptions] = useState<Usuario[]>([]);
  const [userInputValue, setUserInputValue] = useState('');
  const [userLoading, setUserLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setNome('');
      setUsername('');
      setError(null);
      setSelectedUser(null);
      setUserOptions([]);
      setUserInputValue('');
    }
  }, [open]);

  useEffect(() => {
    if (!userInputValue.trim()) {
      setUserOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setUserLoading(true);
        const users = await buscarUsuarios(userInputValue);
        setUserOptions(users.filter(u => u.ativo));
      } catch (err) {
        console.error('Erro ao buscar usuários:', err);
      } finally {
        setUserLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userInputValue]);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const validate = () => {
    if (!nome.trim()) {
      setError('Nome é obrigatório.');
      return false;
    }
    if (!selectedUser) {
      setError('Selecione um organizador para a liga.');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    setError(null);
    try {
      const input: LigaInput = {
        nome: nome.trim(),
        username: username.trim() || null,
      };
      const liga = await onCreate(input);
      
      // Create organizer for the league
      await criarOrganizadorLiga({
        ligaId: liga.id,
        usuarioId: selectedUser!.id,
        nome: selectedUser!.nome,
        nomeFuncao: 'ADMINISTRADOR',
      });
      
      onClose();
    } catch (err: any) {
      const msg = err?.message || 'Erro ao criar liga.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isSaveDisabled =
    loading || !nome.trim() || !selectedUser;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="create-league-title"
    >
      <DialogTitle id="create-league-title">Criar Liga</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Nome da liga"
            required
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            helperText="Identificador único da liga, sem espaços (opcional)."
          />
          <Autocomplete
            options={userOptions}
            getOptionLabel={(option) => `${option.nome} (${option.email})`}
            value={selectedUser}
            onChange={(_, newValue) => setSelectedUser(newValue)}
            inputValue={userInputValue}
            onInputChange={(_, newInputValue) => setUserInputValue(newInputValue)}
            loading={userLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Organizador"
                required
                helperText="Selecione o usuário que será o administrador da liga."
              />
            )}
            noOptionsText={userInputValue ? "Nenhum usuário encontrado" : "Digite para buscar usuários"}
          />
          
          {error && (
            <div className="text-danger small">
              {error}
            </div>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaveDisabled}
          variant="contained"
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLeagueDialog;







